import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (!token) { setError("This reset link is missing its token."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#FFFFFF] border border-[#E7DFD5] px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300";

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 border-r border-[#E7DFD5] relative overflow-hidden">
        <div className="section-tag">§ Recover access</div>
        <div>
          <h1 className="font-display text-6xl xl:text-7xl leading-[0.9] tracking-tight mb-6">
            Choose a new <span className="italic text-[#B45309]">key</span>.
          </h1>
          <p className="text-[#78716C] text-lg leading-relaxed max-w-md">
            Make it something you'll remember but nobody else can guess.
          </p>
        </div>
        <div className="font-display text-[9vw] leading-none text-[#44403C]">T.A.C.C.<span className="text-[#B45309]">.</span></div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
          className="w-full max-w-md pt-24 lg:pt-0"
          data-testid="reset-password-page"
        >
          <div className="section-tag mb-4">Set a new password</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            New password.
          </h2>

          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">New password</label>
              <input
                data-testid="reset-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Confirm password</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputCls}
                placeholder="Type it again"
              />
            </div>

            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}

            <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-ember text-sm w-full justify-center">
              {loading ? "Saving…" : "Reset password"}
              <ArrowUpRight size={16} />
            </button>
          </form>

          <Link to="/login" className="mt-8 inline-block tacc-link text-[#B45309] text-sm">← Back to sign in</Link>
        </motion.div>
      </div>
    </main>
  );
}
